import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';
import { formatDateTime, calculateImpactTime } from '../services/date';

const THREE_MONTHS_MS = 3 * 30 * 24 * 60 * 60 * 1000;

const TYPE_BADGE = {
  informational: { label: 'Informational', className: 'border-2 border-blue-500 text-blue-500' },
  maintenance: { label: 'Maintenance', className: 'border-2 border-red-800 text-red-800' },
};

function getTypeBadge(type) {
  const key = (type || 'maintenance').toLowerCase();
  return TYPE_BADGE[key] || TYPE_BADGE['maintenance'];
}

function isWithin3Months(dateStr) {
  if (!dateStr) return false;
  return Date.now() - new Date(dateStr).getTime() <= THREE_MONTHS_MS;
}

export default function HistoryPage({ pastIncidents, archivedMaintenance, onEraseIncident, onEraseMaintenance, onBack, backLabel, clusterAliasMap = {} }) {
  const [activeTab, setActiveTab] = useState('incidents');

  const recentIncidents = (pastIncidents || []).filter(inc =>
    isWithin3Months(inc.end_time || inc.modified || inc.created)
  );

  const maintenanceList = Array.isArray(archivedMaintenance)
    ? archivedMaintenance
    : archivedMaintenance?.maintenance_list || [];
  const recentNotices = maintenanceList.filter(m =>
    isWithin3Months(m.archive_date || m.end_time || m.start_time)
  );

  return (
    <div className="min-h-screen bg-white">
      <header className="py-8">
        <div className="max-w-4xl mx-auto px-4 flex justify-between items-center">
          <img src="/cv_logo.png" alt="Logo" className="h-12" />
          <img src="/agni-blue.png" alt="AGNI Status Page" className="h-[74px]" />
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 pb-12">
        <button
          onClick={onBack}
          className="text-blue-600 hover:text-blue-800 hover:underline text-sm mb-6 flex items-center gap-1"
        >
          ← {backLabel || 'Back to Status'}
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Incident &amp; Notice History</h1>
        <p className="text-sm text-gray-400 mb-6">Showing records from the past 3 months only.</p>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('incidents')}
            className={`px-3 py-2 sm:px-6 sm:py-3 text-xs sm:text-sm font-medium border border-b-0 rounded-tl rounded-tr -mb-px ${
              activeTab === 'incidents'
                ? 'bg-white border-gray-200 text-blue-600'
                : 'bg-gray-50 border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Past Incidents
          </button>
          <button
            onClick={() => setActiveTab('notices')}
            className={`px-3 py-2 sm:px-6 sm:py-3 text-xs sm:text-sm font-medium border border-b-0 rounded-tl rounded-tr -mb-px ml-1 ${
              activeTab === 'notices'
                ? 'bg-white border-gray-200 text-blue-600'
                : 'bg-gray-50 border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            Past Notices
          </button>
        </div>

        {/* Past Incidents tab */}
        {activeTab === 'incidents' && (
          <div>
            {recentIncidents.length === 0 ? (
              <p className="text-sm text-gray-500 italic">None in the past 3 months.</p>
            ) : (
              <div className="space-y-4">
                {recentIncidents.map(inc => {
                  const impactTime = calculateImpactTime(inc.created, inc.end_time);
                  return (
                    <div key={inc.id} className="border border-gray-200 rounded p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                        <div className="flex-grow min-w-0">
                          <p className="font-bold text-gray-800">{inc.description}</p>
                          <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                            {inc.created && <span>Created: {formatDateTime(inc.created)}</span>}
                            {inc.start_time && <span>Started: {formatDateTime(inc.start_time)}</span>}
                            {inc.end_time && <span>Ended: {formatDateTime(inc.end_time)}</span>}
                            {impactTime && (
                              <span className="font-medium text-gray-700">Total Impact: {impactTime}</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                            {inc.status && <span><strong>Status:</strong> {inc.status}</span>}
                            {inc.severity && <span><strong>Severity:</strong> {inc.severity}</span>}
                          </div>
                          {inc.most_recent_update_text && (
                            <div className="prose prose-sm max-w-full mt-2 text-gray-700">
                              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                                {inc.most_recent_update_text}
                              </ReactMarkdown>
                            </div>
                          )}
                          {inc.affected_services && inc.affected_services.filter(s => s).length > 0 && (
                            <p className="text-xs text-gray-500 mt-1"><strong>Affected Services:</strong> {inc.affected_services.filter(s => s).join(', ')}</p>
                          )}
                          {inc.affected_clusters && inc.affected_clusters.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1"><strong>Affected Clusters:</strong> {inc.affected_clusters.map(c => clusterAliasMap[c] || c).join(', ')}</p>
                          )}
                        </div>
                        {onEraseIncident && (
                          <button
                            onClick={() => onEraseIncident(inc)}
                            className="self-start sm:ml-4 px-4 py-2 bg-red-700 text-white rounded hover:bg-red-800 shrink-0"
                          >
                            Erase
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Past Notices tab */}
        {activeTab === 'notices' && (
          <div>
            {recentNotices.length === 0 ? (
              <p className="text-sm text-gray-500 italic">None in the past 3 months.</p>
            ) : (
              <div className="space-y-4">
                {recentNotices.map(m => {
                  const badge = getTypeBadge(m.type);
                  return (
                    <div key={m.id} className="border border-gray-200 rounded p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
                        <div className="flex-grow min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-bold text-gray-800">{m.title}</p>
                            <span className={`text-sm font-bold px-2.5 py-1 rounded-none shrink-0 ${badge.className}`}>
                              {badge.label}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                            {m.start_time && <span>Started: {formatDateTime(m.start_time)}</span>}
                            {m.end_time && <span>Ended: {formatDateTime(m.end_time)}</span>}
                            {m.archive_date && <span>Archived: {formatDateTime(m.archive_date)}</span>}
                          </div>
                          {m.most_recent_update && (
                            <div className="prose prose-sm max-w-full mt-2 text-gray-700">
                              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
                                {m.most_recent_update}
                              </ReactMarkdown>
                            </div>
                          )}
                          {m.clusters && m.clusters.length > 0 && (
                            <p className="text-xs text-gray-500 mt-1"><strong>Affected Clusters:</strong> {m.clusters.map(c => clusterAliasMap[c] || c).join(', ')}</p>
                          )}
                        </div>
                        {onEraseMaintenance && (
                          <button
                            onClick={() => onEraseMaintenance(m)}
                            className="self-start sm:ml-4 px-4 py-2 bg-red-700 text-white rounded hover:bg-red-800 shrink-0"
                          >
                            Erase
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
